import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {APIService} from "./api.service";
import {ICharityPayee} from "../interfaces/payee/ICharityPayee";
import {ICharityOwedForPayee} from "../interfaces/payee/ICharityOwedForPayee";
import {IRecordPayeePayoutResult} from "../interfaces/payee/IRecordPayeePayoutResult";
import {ICharityPayeeAssignment} from "../interfaces/payee/ICharityPayeeAssignment";
import {IPayeePaymentSummary} from "../interfaces/payee/IPayeePaymentSummary";

/**
 * Central payees (#1411): the accounts a charity's money is remitted to when the cheque is not made out to the
 * charity itself.
 */
@Injectable({providedIn: 'root'})
export class CharityPayeeService {

  constructor(private apiService: APIService) {
  }

  public fetchByGameId(gameIdP: string): Observable<ICharityPayee[]> {
    return this.apiService.MakeGetRequest<ICharityPayee[]>(`charity-payee/by-game/${gameIdP}`);
  }

  /**
   * Every charity in the game with the payee it is paid through. Its own admin endpoint rather than the
   * charity list the rest of the app uses, which is served unauthenticated to the player apps.
   */
  public fetchAssignments(gameIdP: string): Observable<ICharityPayeeAssignment[]> {
    return this.apiService.MakeGetRequest<ICharityPayeeAssignment[]>(`charity-payee/assignments/by-game/${gameIdP}`);
  }

  /** The payee's charities and what each is owed — the parts a payment to it will be split into. */
  public fetchMembers(payeeIdP: string, gameIdP: string): Observable<ICharityOwedForPayee[]> {
    return this.apiService.MakeGetRequest<ICharityOwedForPayee[]>(`charity-payee/${payeeIdP}/members?GameId=${gameIdP}`);
  }

  /** Every payment made to this payee, newest first — what an admin picks from to void a mis-keyed cheque. */
  public fetchPayments(payeeIdP: string, gameIdP: string): Observable<IPayeePaymentSummary[]> {
    return this.apiService.MakeGetRequest<IPayeePaymentSummary[]>(`charity-payee/${payeeIdP}/payments?GameId=${gameIdP}`);
  }

  /**
   * Voids a whole payment. There is deliberately no way to void or edit one charity's share: the rows are one
   * cheque, so correcting it means voiding the payment and recording it again.
   */
  public voidPayment(gameIdP: string, payoutBatchIdP: string): Observable<void> {
    return this.apiService.MakePutRequest<void>('charity-payee/payout/void',
      {GameId: gameIdP, PayoutBatchId: payoutBatchIdP});
  }

  public createPayee(gameIdP: string, nameP: string): Observable<void> {
    return this.apiService.MakePostRequest<void>('charity-payee', {GameId: gameIdP, Name: nameP});
  }

  public updatePayee(gameIdP: string, payeeIdP: string, nameP: string, activeP: boolean): Observable<void> {
    return this.apiService.MakePutRequest<void>('charity-payee',
      {GameId: gameIdP, CharityPayeeId: payeeIdP, Name: nameP, Active: activeP});
  }

  /** Assigns a charity to a payee, or clears it with null so the charity is paid directly. */
  public assignCharity(gameIdP: string, charityIdP: string, payeeIdP: string | null): Observable<void> {
    return this.apiService.MakePutRequest<void>('charity-payee/assign-charity',
      {GameId: gameIdP, CharityId: charityIdP, CharityPayeeId: payeeIdP});
  }

  /**
   * Records one payment to a payee. Only the total is sent: the split across member charities is the server's
   * to compute, which is what stops the parts disagreeing with the cheque.
   */
  public recordPayeePayout(commandP: {
    GameId: string,
    CharityPayeeId: string,
    PayoutAmount: number,
    PaymentDate: string | null,
    ReferenceNumber: string | null,
    Note: string | null
  }): Observable<IRecordPayeePayoutResult> {
    return this.apiService.MakePostRequest<IRecordPayeePayoutResult>('charity-payee/payout', commandP);
  }
}
